import { useMutation, ApolloError } from "@apollo/client";
import React from "react";

import { REFRESH_TOKEN } from "graphql/query/authorization";
import { getRefreshTokenKey, isAllowedToRefreshToken, logoutUser, saveAppAuthorizationToken } from "utils/auth";
import { IRefreshTokenResponseDTO } from "dto/authorization/refreshToken.dto";

type TypeCallRefreshToken = (isRecalledRef: React.MutableRefObject<boolean>) => void;

interface IRefreshTokenGQLVariables {
  refreshToken: string;
}

interface IUseRefreshToken {
  onCompleted?: (response: IRefreshTokenResponseDTO) => void;
  onError?: (error: ApolloError) => void;
}

interface IUseRefreshTokenResponse {
  called: boolean;
  callRefreshToken: TypeCallRefreshToken;
  loading: boolean;
}

const useRefreshToken: (arg: IUseRefreshToken) => IUseRefreshTokenResponse = ({ onCompleted, onError }) => {
  const [documentNode, { called, loading }] = useMutation(REFRESH_TOKEN, {
    fetchPolicy: "no-cache",
    onCompleted: async data => {
      const response = data.response as IRefreshTokenResponseDTO;
      await saveAppAuthorizationToken(response);

      if (onCompleted) {
        onCompleted(response);
      }
    },
    onError: error => {
      if (onError) {
        onError(error);
      }
    },
  });

  const callRefreshToken: TypeCallRefreshToken = async isRecalledRef => {
    const canRefreshToken = await isAllowedToRefreshToken();
    const dontRefreshToken = isRecalledRef.current || !canRefreshToken;

    if (dontRefreshToken) {
      logoutUser({
        isReloadPage: true,
      });
    } else {
      if (!loading) {
        const refreshToken = getRefreshTokenKey();
        const gqlVariables: IRefreshTokenGQLVariables = {
          refreshToken,
        };

        documentNode({
          variables: gqlVariables,
        });
      }
    }
  };

  return {
    called,
    callRefreshToken,
    loading,
  };
};

export default useRefreshToken;
